/* Importação de histórico em papel.
 *
 * Cópia do modal de criação normal com uma diferença: cada linha escolhe também
 * o DENTISTA que a executou. Na criação normal isso não faz sentido — quem
 * regista está a registar o que acabou de fazer — mas ao copiar uma ficha
 * antiga o trabalho foi de quem foi, e pode nem trabalhar cá hoje.
 *
 * Não notifica a equipa: avisar alguém de um tratamento de 2023 é ruído. E
 * grava por uma rota própria, que marca os registos como importados.
 *
 * Só aparece com «Importação de Histórico» ligada em Configurações → Outras. */
import React, { useState, useEffect, useContext } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import moment from "moment";
import { ClinicaContext } from "../../../../../../../../shared/context/clinica-context";
import { useLoading } from "../../../../../../../../shared/context/LoadingContext";
import toast from "react-hot-toast";

import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import OutlinedInput from "@mui/material/OutlinedInput";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const facesDentes = ["D", "M", "O/I", "P/L", "V"];

const todosOsDentes = [
  "11",
  "12",
  "13",
  "14",
  "15",
  "16",
  "17",
  "18",
  "21",
  "22",
  "23",
  "24",
  "25",
  "26",
  "27",
  "28",
  "31",
  "32",
  "33",
  "34",
  "35",
  "36",
  "37",
  "38",
  "41",
  "42",
  "43",
  "44",
  "45",
  "46",
  "47",
  "48",
  "51",
  "52",
  "53",
  "54",
  "55",
  "61",
  "62",
  "63",
  "64",
  "65",
  "71",
  "72",
  "73",
  "74",
  "75",
  "81",
  "82",
  "83",
  "84",
  "85",
  "Arcada Superior",
  "Arcada Inferior",
  "Arcadas",
];

const criarItemVazio = () => ({
  id: Date.now() + Math.random(),
  selectedTratamento: "",
  dentes: [],
  faces: [],
  showDentes: false,
  showFaces: false,
  listaDentes: todosOsDentes,
  /* Ao contrário da criação normal, a data por omissão é ONTEM.
     Quem está a copiar uma ficha em papel está sempre a lançar o passado, e
     deixar a hora seguinte por omissão convidava a gravar um tratamento antigo
     com a data de hoje sem se dar por isso. */
  dataInicioExecucao: moment().subtract(1, "day").startOf("hour"),
  dataFimExecucao: moment().subtract(1, "day").startOf("hour").add(1, "hour"),
  observacao: "",
  medico: "",
});

function ModalImportarHistorico({
  sendRequest,
  auth,
  clienteId,
  open,
  handleCloseModal,
  handleModalSubmitSuccess,
}) {
  const clinica = useContext(ClinicaContext);
  const { startLoading, stopLoading } = useLoading();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tratamentosLista, setTratamentosLista] = useState([criarItemVazio()]);

  const [tratamentos, setTratamentos] = useState([]);
  // Os dentistas da clínica, para se dizer quem executou cada linha.
  const [dentistas, setDentistas] = useState([]);

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      fetchDados();
    }
  }, [open]);

  const handleTratamentoChange = (id, tratamentoId) => {
    setTratamentosLista((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;

        if (tratamentoId) {
          const tratamento = tratamentos.find((t) => t._id === tratamentoId);

          if (tratamento) {
            const dentesEspecificos =
              tratamento.dentesEspecificos &&
              tratamento.dentesEspecificos.trim() !== ""
                ? tratamento.dentesEspecificos.split(", ")
                : [];

            return {
              ...item,
              selectedTratamento: tratamentoId,
              showDentes: tratamento.aceitaDentes,
              showFaces: tratamento.aceitaFaces,
              listaDentes:
                dentesEspecificos.length > 0
                  ? dentesEspecificos
                  : todosOsDentes,
              dentes: dentesEspecificos.length === 1 ? dentesEspecificos : [],
              faces: [],
            };
          }
        }
        return {
          ...item,
          selectedTratamento: "",
          showDentes: false,
          showFaces: false,
          dentes: [],
          faces: [],
          listaDentes: todosOsDentes,
        };
      })
    );
    setErrors((prev) => ({ ...prev, [`tratamento_${id}`]: "" }));
  };

  const handleDentesChange = (id, value) => {
    const newDentes = typeof value === "string" ? value.split(",") : value;
    setTratamentosLista((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, dentes: newDentes } : item
      )
    );
    setErrors((prev) => ({ ...prev, [`dentes_${id}`]: "" }));
  };

  const handleFacesChange = (id, value) => {
    const newFaces = typeof value === "string" ? value.split(",") : value;
    setTratamentosLista((prev) =>
      prev.map((item) => (item.id === id ? { ...item, faces: newFaces } : item))
    );
    setErrors((prev) => ({ ...prev, [`faces_${id}`]: "" }));
  };

  const handleDataInicioChange = (id, value) => {
    setTratamentosLista((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, dataInicioExecucao: value } : item
      )
    );
    setErrors((prev) => ({ ...prev, [`dataInicio_${id}`]: "" }));
  };

  const handleDataFimChange = (id, value) => {
    setTratamentosLista((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, dataFimExecucao: value } : item
      )
    );
    setErrors((prev) => ({ ...prev, [`dataFim_${id}`]: "" }));
  };

  const handleMedicoChange = (id, value) => {
    setTratamentosLista((prev) =>
      prev.map((item) => (item.id === id ? { ...item, medico: value } : item))
    );
    setErrors((prev) => ({ ...prev, [`medico_${id}`]: "" }));
  };

  const handleObservacaoChange = (id, value) => {
    setTratamentosLista((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, observacao: value } : item
      )
    );
  };

  const adicionarTratamento = () => {
    setTratamentosLista((prev) => {
      const anterior = prev[prev.length - 1];
      const novoItem = criarItemVazio();
      // A data/hora do novo tratamento vem, por definição, igual à do anterior
      if (anterior) {
        novoItem.dataInicioExecucao = anterior.dataInicioExecucao;
        novoItem.dataFimExecucao = anterior.dataFimExecucao;
      }
      return [...prev, novoItem];
    });
  };

  const removerTratamento = (id) => {
    if (tratamentosLista.length > 1) {
      setTratamentosLista((prev) => prev.filter((item) => item.id !== id));
    }
  };

  const fetchDados = async () => {
    try {
      startLoading();

      // Buscar tratamentos
      const tratamentosResponse = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/tratamentos/clinica/${clinica.clinica._id}`,
        "GET",
        null,
        { Authorization: "Bearer " + auth.token }
      );

      const tratamentosOrdenados = tratamentosResponse.tratamentos
        .filter((t) => t.ativo)
        .sort((a, b) => a.designacao.trim().localeCompare(b.designacao.trim()));

      setTratamentos(tratamentosOrdenados);

      /* Todos os dentistas, INCLUINDO os inativos — endpoint próprio, distinto
         do que o resto da aplicação usa. Uma ficha de 2022 tem trabalho de quem
         entretanto saiu, e sem eles na lista esse trabalho ficava sem se poder
         atribuir a quem o fez. Em mais lado nenhum se devem oferecer
         ex-colegas: só aqui, porque só aqui se escreve o passado. */
      const dentistasResponse = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/users/dentistas/todos/${clinica.clinica._id}`,
        "GET",
        null,
        { Authorization: "Bearer " + auth.token }
      );

      setDentistas(dentistasResponse.dentistas || []);
    } catch (err) {
      console.error("Erro ao buscar dados:", err);
      toast.error("Erro ao carregar dados");
    } finally {
      stopLoading();
    }
  };

  const validarFormulario = () => {
    const newErrors = {};

    tratamentosLista.forEach((item) => {
      /* Obrigatório, e é o que distingue este ecrã do normal: na criação do dia
         a dia o dentista deduz-se de quem está a registar, aqui não há nada de
         onde o deduzir. Um procedimento histórico sem autor não serve para o
         que o histórico serve. */
      if (!item.medico) {
        newErrors[`medico_${item.id}`] = "Escolha quem executou";
      }

      if (!item.selectedTratamento) {
        newErrors[`tratamento_${item.id}`] = "Tratamento é obrigatório";
      } else {
        const tratamento = tratamentos.find(
          (t) => t._id === item.selectedTratamento
        );

        if (tratamento && tratamento.aceitaDentes) {
          if (item.dentes.length === 0) {
            newErrors[`dentes_${item.id}`] =
              "Por favor selecione os dentes a tratar";
          } else if (
            tratamento.multiDentes &&
            item.dentes.length % tratamento.qtdDentes !== 0
          ) {
            newErrors[
              `dentes_${item.id}`
            ] = `O tratamento ${tratamento.designacao} requer múltiplos de ${tratamento.qtdDentes} dentes. Foram selecionados ${item.dentes.length} dentes.`;
          }
        }

        if (tratamento && tratamento.aceitaFaces && item.showFaces) {
          const dentesComFaces = !item.dentes.some((d) =>
            ["Arcadas", "Arcada Superior", "Arcada Inferior"].includes(d)
          );
          if (dentesComFaces && item.faces.length === 0) {
            newErrors[`faces_${item.id}`] =
              "Por favor selecione as faces a tratar";
          } else if (
            dentesComFaces &&
            tratamento.qtdFaces < 5 &&
            item.faces.length !== tratamento.qtdFaces
          ) {
            newErrors[
              `faces_${item.id}`
            ] = `O tratamento ${tratamento.designacao} exige exatamente ${tratamento.qtdFaces} face(s). Foram selecionadas ${item.faces.length}.`;
          }
        }
      }

      // Validação das datas POR tratamento
      if (!item.dataInicioExecucao) {
        newErrors[`dataInicio_${item.id}`] = "Data de início é obrigatória";
      }
      if (!item.dataFimExecucao) {
        newErrors[`dataFim_${item.id}`] = "Data de fim é obrigatória";
      }
      if (
        item.dataInicioExecucao &&
        item.dataFimExecucao &&
        item.dataInicioExecucao.isAfter(item.dataFimExecucao)
      ) {
        newErrors[`dataFim_${item.id}`] =
          "Data de fim deve ser posterior à data de início";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submitHandler = async () => {
    if (!validarFormulario()) {
      toast.error("Por favor corrija os erros antes de continuar");
      return;
    }

    setIsSubmitting(true);
    startLoading();

    try {
      const tratamentosData = tratamentosLista.map((item) => {
        const data = {
          cliente: clienteId,
          clinica: clinica.clinica._id,
          // Quem executou, escolhido linha a linha — não quem está a importar.
          medico: item.medico,
          data_inicio_execucao: item.dataInicioExecucao.toISOString(),
          data_fim_execucao: item.dataFimExecucao.toISOString(),
          tratamento: item.selectedTratamento,
        };

        if (item.dentes.length > 0) {
          data.dente = item.dentes.join(",");
        }

        if (item.faces.length > 0) {
          data.faces = item.faces;
        }

        // O texto deste campo é o relatório do tratamento (mostrado na
        // visualização e no histórico).
        if (item.observacao && item.observacao.trim() !== "") {
          data.relatorio = item.observacao.trim();
        }

        return data;
      });

      const formData = new FormData();
      formData.append(
        "tratamentosIsoladosData",
        JSON.stringify(tratamentosData)
      );

      /* Rota da importação: é ela que marca os registos como histórico
         importado. A criação normal escreve na `datasindividuais`. */
      const response = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/tratamentoIsolados/multiplos/fichacliente/historico/${auth.userId}`,
        "POST",
        formData,
        { Authorization: "Bearer " + auth.token }
      );

      const tratamentosIsoladosCriados = response.tratamentosIsolados;

      /* Sem notificação, ao contrário da criação normal.
         Quem importa uma ficha lança dezenas de procedimentos de anos
         passados; avisar a equipa de cada um deles enche o sino de coisas que
         já aconteceram, e um aviso que toca por coisas velhas é um aviso que se
         aprende a ignorar. */

      toast.success(
        `${tratamentosIsoladosCriados.length} procedimento(s) importado(s) para o histórico`
      );
      handleModalSubmitSuccess(tratamentosIsoladosCriados);
      handleCloseModal();
      resetForm();
    } catch (err) {
      console.error("Erro ao criar tratamentos isolados:", err);
      toast.error(err.message || "Erro ao criar tratamentos isolados");
    } finally {
      setIsSubmitting(false);
      stopLoading();
    }
  };

  const resetForm = () => {
    setTratamentosLista([criarItemVazio()]);
    setErrors({});
  };

  return (
    <Modal
      open={open}
      onClose={handleCloseModal}
      aria-labelledby="modal-criar-tratamento"
      aria-describedby="modal-criar-tratamento-desc"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 480,
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 2.5,
          borderRadius: 2,
          maxHeight: "90vh",
          overflow: "auto",
        }}
      >
        <h3 style={{ marginTop: 0, marginBottom: 12 }}>
          Importar Histórico
        </h3>

        <Alert severity="warning" sx={{ mb: 2, py: 0.5 }}>
          Para registar tratamentos <strong>já feitos</strong>, copiados de uma
          ficha em papel. Cada um com a sua data e o dentista que o executou.
          Não gera dívida nem desconta stock, e fica marcado no histórico como{" "}
          <strong>importado</strong>.
        </Alert>

        {tratamentosLista.map((item, index) => (
          <Box
            key={item.id}
            sx={{
              mb: 1.5,
              p: 1.5,
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              position: "relative",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 12,
              }}
            >
              <strong>Tratamento {index + 1}</strong>
              {tratamentosLista.length > 1 && (
                <Button
                  size="small"
                  color="error"
                  onClick={() => removerTratamento(item.id)}
                  sx={{ textTransform: "none" }}
                >
                  Remover
                </Button>
              )}
            </div>

            <FormControl
              fullWidth
              sx={{ mb: 2 }}
              error={!!errors[`tratamento_${item.id}`]}
            >
              <InputLabel id={`tratamento-label-${item.id}`}>
                Tratamento*
              </InputLabel>
              <Select
                labelId={`tratamento-label-${item.id}`}
                value={item.selectedTratamento}
                onChange={(e) =>
                  handleTratamentoChange(item.id, e.target.value)
                }
                label="Tratamento*"
              >
                <MenuItem value="">Selecione um tratamento</MenuItem>
                {tratamentos.map((tratamento) => (
                  <MenuItem key={tratamento._id} value={tratamento._id}>
                    {tratamento.designacao}
                    {tratamento.fornecedor?.nome
                      ? ` (${tratamento.fornecedor.nome})`
                      : ""}{" "}
                    - {tratamento.preco_particular.toLocaleString("pt-Br")} CVE
                  </MenuItem>
                ))}
              </Select>
              {errors[`tratamento_${item.id}`] && (
                <span
                  style={{
                    color: "#d32f2f",
                    fontSize: "0.75rem",
                    marginTop: "3px",
                  }}
                >
                  {errors[`tratamento_${item.id}`]}
                </span>
              )}
            </FormControl>

            {item.showDentes && (
              <FormControl
                fullWidth
                sx={{ mb: 2 }}
                error={!!errors[`dentes_${item.id}`]}
              >
                <InputLabel id={`dentes-label-${item.id}`}>Dentes*</InputLabel>
                <Select
                  labelId={`dentes-label-${item.id}`}
                  multiple
                  value={item.dentes}
                  onChange={(e) => handleDentesChange(item.id, e.target.value)}
                  input={<OutlinedInput label="Dentes*" />}
                  renderValue={(selected) => selected.join(", ")}
                  MenuProps={MenuProps}
                >
                  {item.listaDentes.map((dente) => (
                    <MenuItem key={dente} value={dente}>
                      <Checkbox checked={item.dentes.indexOf(dente) > -1} />
                      <ListItemText primary={dente} />
                    </MenuItem>
                  ))}
                </Select>
                {errors[`dentes_${item.id}`] && (
                  <span
                    style={{
                      color: "#d32f2f",
                      fontSize: "0.75rem",
                      marginTop: "3px",
                    }}
                  >
                    {errors[`dentes_${item.id}`]}
                  </span>
                )}
              </FormControl>
            )}

            {item.showFaces &&
              !item.dentes.some((d) =>
                ["Arcadas", "Arcada Superior", "Arcada Inferior"].includes(d)
              ) && (
                <FormControl
                  fullWidth
                  sx={{ mb: 2 }}
                  error={!!errors[`faces_${item.id}`]}
                >
                  <InputLabel id={`faces-label-${item.id}`}>Faces*</InputLabel>
                  <Select
                    labelId={`faces-label-${item.id}`}
                    multiple
                    value={item.faces}
                    onChange={(e) => handleFacesChange(item.id, e.target.value)}
                    input={<OutlinedInput label="Faces*" />}
                    renderValue={(selected) => selected.join(", ")}
                    MenuProps={MenuProps}
                  >
                    {facesDentes.map((face) => (
                      <MenuItem key={face} value={face}>
                        <Checkbox checked={item.faces.indexOf(face) > -1} />
                        <ListItemText primary={face} />
                      </MenuItem>
                    ))}
                  </Select>
                  {errors[`faces_${item.id}`] && (
                    <span
                      style={{
                        color: "#d32f2f",
                        fontSize: "0.75rem",
                        marginTop: "3px",
                      }}
                    >
                      {errors[`faces_${item.id}`]}
                    </span>
                  )}
                </FormControl>
              )}

            {/* Quem executou ESTE procedimento. É o campo que só existe aqui:
                ao copiar uma ficha antiga, cada linha pode ter sido de um
                dentista diferente, incluindo quem já saiu da clínica. */}
            <FormControl fullWidth style={{ marginBottom: 12 }}>
              <InputLabel id={`medico-label-${item.id}`}>
                Executado por*
              </InputLabel>
              <Select
                labelId={`medico-label-${item.id}`}
                label="Executado por*"
                value={item.medico}
                onChange={(e) => handleMedicoChange(item.id, e.target.value)}
                error={!!errors[`medico_${item.id}`]}
                MenuProps={MenuProps}
              >
                {dentistas.map((d) => (
                  <MenuItem key={d._id} value={d._id}>
                    {d.name}
                    {d.ativo === false ? " (inativo)" : ""}
                  </MenuItem>
                ))}
              </Select>
              {errors[`medico_${item.id}`] && (
                <span className="modalCriarTratamentoIsolado__error">
                  {errors[`medico_${item.id}`]}
                </span>
              )}
            </FormControl>

            {/* Data de execução INDIVIDUAL deste tratamento */}
            <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DateTimePicker
                  label="Início*"
                  inputFormat="DD-MM-YYYY HH:mm"
                  value={item.dataInicioExecucao}
                  onChange={(value) => handleDataInicioChange(item.id, value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      error={!!errors[`dataInicio_${item.id}`]}
                      helperText={errors[`dataInicio_${item.id}`]}
                    />
                  )}
                />
              </LocalizationProvider>

              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DateTimePicker
                  label="Fim*"
                  inputFormat="DD-MM-YYYY HH:mm"
                  value={item.dataFimExecucao}
                  onChange={(value) => handleDataFimChange(item.id, value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      error={!!errors[`dataFim_${item.id}`]}
                      helperText={errors[`dataFim_${item.id}`]}
                    />
                  )}
                />
              </LocalizationProvider>
            </div>

            {/* Observação INDIVIDUAL deste tratamento */}
            <TextField
              fullWidth
              multiline
              rows={2}
              size="small"
              label="Observação"
              placeholder="Observação sobre este tratamento..."
              value={item.observacao}
              onChange={(e) => handleObservacaoChange(item.id, e.target.value)}
              variant="outlined"
            />
          </Box>
        ))}

        <Button
          variant="outlined"
          onClick={adicionarTratamento}
          sx={{ mb: 2, textTransform: "none" }}
          fullWidth
        >
          + Adicionar outro tratamento
        </Button>

        {/* Botões de ação */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            marginTop: 8,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => {
              handleCloseModal();
              resetForm();
            }}
            sx={{ textTransform: "none" }}
          >
            Cancelar
          </Button>
          <Button
            disabled={isSubmitting}
            variant="contained"
            onClick={submitHandler}
            sx={{ textTransform: "none" }}
          >
            {isSubmitting ? "Criando..." : "Criar e Executar Tratamento"}
          </Button>
        </div>
      </Box>
    </Modal>
  );
}

export default ModalImportarHistorico;
